// RiverLongitudinalProfileMultiRiver.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Tooltip as LTooltip,
} from "react-leaflet";
import L from "leaflet";

import wqmsService from "../services/wqmsService";
import "./RiverParameterComparison.css";

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = "Asia/Karachi";

/* ================= RIVERS ================= */

const RIVERS = ["Ravi", "Chenab", "Jhelum", "Sutlej", "Indus", "Punjnad"];

const RIVER_COLORS = {
  Ravi: "#2563eb",
  Chenab: "#16a34a",
  Jhelum: "#d97706",
  Sutlej: "#dc2626",
  Indus: "#7c3aed",
  Punjnad: "#0891b2",
};

const SOFT_COLORS = [
  "#60a5fa", // blue
  "#34d399", // green
  "#fbbf24", // amber
  "#f472b6", // pink
  "#a78bfa", // violet
  "#22d3ee", // cyan
  "#fb7185", // rose
  "#94a3b8", // slate
];


/* ================= WQMS STATION LABELS ================= */

const WQMS_LABELS = {
  1:  { code: "WQMS1",  name: "Ravi - Siphon Lahore" },
  2:  { code: "WQMS2",  name: "Sutlej - Sulemanki Headworks" },
  3:  { code: "WQMS3",  name: "Chenab - Head Marala Sialkot" },
  4:  { code: "WQMS4",  name: "Jhelum - Sarai Alamgir" },
  5:  { code: "WQMS5",  name: "Indus - Mianwali Kala Bagh" },
  6:  { code: "WQMS6",  name: "Chenab - Trimmu Headworks Jhang" },
  7:  { code: "WQMS7",  name: "Ravi - Hadiara Drain Lahore" },
  8:  { code: "WQMS8",  name: "Ravi - Mudhwana Drain Chicha Watni" },
  9:  { code: "WQMS9",  name: "Sutlej - Kali Budhi Drain Pakpattan" },
  10: { code: "WQMS10", name: "Sutlej - Islam Headworks Hasilpur" },
  11: { code: "WQMS11", name: "Chenab - Khanki Barrage Wazirabad" },
  12: { code: "WQMS12", name: "Chenab - Pharang Drain Chiniot" },
  13: { code: "WQMS13", name: "Jhelum - Mona Bhera Drain Bhera" },
  14: { code: "WQMS14", name: "Chenab - Budhi Drain Mandi Bahauddin" },
  15: { code: "WQMS15", name: "Punjnad - Headworks Zahir Pir" },
};

/* ================= PARAM DEFINITIONS ================= */

const PARAM_DEFS = [
  { base: "ph", measured: "ph_avg", clean: "ph_clean_avg", label: "pH" },

  {
    base: "temperature_c",
    measured: "temperature_c_avg",
    clean: "temperature_c_clean_avg",
    label: "Temperature (°C)",
  },
  {
    base: "dissolved_oxygen_mg_l",
    measured: "dissolved_oxygen_mg_l_avg",
    clean: "dissolved_oxygen_mg_l_clean_avg",
    label: "Dissolved Oxygen (mg/L)",
  },
  {
    base: "turbidity_ntu",
    measured: "turbidity_ntu_avg",
    clean: "turbidity_ntu_clean_avg",
    label: "Turbidity (NTU)",
  },
  {
    base: "ammonia_mg_l",
    measured: "ammonia_mg_l_avg",
    clean: "ammonia_mg_l_clean_avg",
    label: "Ammonia (NH₃) (mg/L)",
  },
  {
    base: "bod_mg_l",
    measured: "bod_mg_l_avg",
    clean: "bod_mg_l_clean_avg",
    label: "BOD (mg/L)",
  },
  {
    base: "cod_mg_l",
    measured: "cod_mg_l_avg",
    clean: "cod_mg_l_clean_avg",
    label: "COD (mg/L)",
  },
  {
    base: "toc_mg_l",
    measured: "toc_mg_l_avg",
    clean: "toc_mg_l_clean_avg",
    label: "TOC (mg/L)",
  },
  {
    base: "doc_mg_l",
    measured: "doc_mg_l_avg",
    clean: "doc_mg_l_clean_avg",
    label: "DOC (mg/L)",
  },
  {
    base: "tss_mg_l",
    measured: "tss_mg_l_avg",
    clean: "tss_mg_l_clean_avg",
    label: "TSS (mg/L)",
  },
  {
    base: "color_hazen",
    measured: "color_hazen_avg",
    clean: "color_hazen_clean_avg",
    label: "Color (Hazen)",
  },
  {
    base: "chloride_mg_l",
    measured: "chloride_mg_l_avg",
    clean: "chloride_mg_l_clean_avg",
    label: "Chloride (mg/L)",
  },
  {
    base: "redox_mv",
    measured: "redox_mv_avg",
    clean: "redox_mv_clean_avg",
    label: "Redox / ORP (mV)",
  },
  {
    base: "conductivity_us_cm",
    measured: "conductivity_us_cm_avg",
    clean: "conductivity_us_cm_clean_avg",
    label: "Conductivity (µS/cm)",
  },
/*  {
    base: "level_m",
    measured: "level_m_avg",
    clean: "level_m_clean_avg",
    label: "Water Level (m)",
  },*/
];


/* ================= MARKER ICON ================= */

const stationIcon = (stationId, color) => {
  const label = WQMS_LABELS[stationId]?.code || `WQMS${stationId}`;

  return L.divIcon({
    className: "rlp-marker-wrapper",
    html: `<div class="rlp-marker-circle" style="background:${color}">${label}</div>`,
    iconSize: [66, 32],
    iconAnchor: [38, 16],
  });
};

/* ================= HELPERS ================= */

const avgByStation = (rows) => {
  const m = new Map();
  rows.forEach((r) => {
    const sid = Number(r.station_id);
    const v = Number(r.value);
    if (!Number.isFinite(v)) return;
    if (!m.has(sid)) m.set(sid, { sum: 0, n: 0 });
    m.get(sid).sum += v;
    m.get(sid).n += 1;
  });
  return m;
};

/* ================= COMPONENT ================= */

const fmtNum = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(3) : "—");

function ProfileTooltip({ active, payload, label, fromDate, toDate, valueMode }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload || {};
  const stationText = row.code
    ? `${row.code}${row.name ? ` — ${row.name}` : ""}`
    : `Station Order: ${label}`;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 12,
        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
        minWidth: 320,
      }}
    >
      {/* ✅ label “1” now visible */}
      <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
        {`Order: ${label}`} · {stationText}
      </div>

      {/* ✅ show selected window */}
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
        Values are the averages for selected period (PKT):{" "}
        <b>{fromDate}</b> to <b>{toDate}</b>
      </div>

      {payload.map((p) => (
        <div
          key={p.dataKey}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            fontSize: 13,
            marginBottom: 6,
          }}
        >
          <span style={{ color: p.color, fontWeight: 700 }}>{p.name}</span>
          <span style={{ color: "#0f172a", fontWeight: 700 }}>{fmtNum(p.value)}</span>
        </div>
      ))}
    </div>
  );
}



export default function RiverLongitudinalProfileMultiRiver() {
  const navigate = useNavigate();

  const [valueMode, setValueMode] = useState("measured");
  const [selectedRivers, setSelectedRivers] = useState(["Jhelum"]);
  const [selectedBaseParams, setSelectedBaseParams] = useState([
    "redox_mv",
    "ammonia_mg_l",
    "bod_mg_l",
    "cod_mg_l",
    "tss_mg_l",
  ]);

  const [fromDate, setFromDate] = useState(dayjs().tz(TZ).subtract(6, "day").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().tz(TZ).format("YYYY-MM-DD"));

  const [stationsByRiver, setStationsByRiver] = useState({});
  const [chartDataByRiver, setChartDataByRiver] = useState({});
  const [loading, setLoading] = useState(false);

  const selectedParams = useMemo(() =>
    selectedBaseParams
      .map((b) => {
        const def = PARAM_DEFS.find((p) => p.base === b);
        return valueMode === "clean" ? def?.clean : def?.measured;
      })
      .filter(Boolean),
    [selectedBaseParams, valueMode]
  );

  useEffect(() => {
    (async () => {
      const out = {};
      for (const river of selectedRivers) {
        const sts = await wqmsService.getStations({ river, timeRange: "all", force: true });
        out[river] = (sts || []).filter(s => s.latitude && s.longitude)
          .sort((a, b) => Number(a.river_order) - Number(b.river_order));
      }
      setStationsByRiver(out);
    })();
  }, [selectedRivers]);

  const generateProfile = async () => {
    setLoading(true);
    const result = {};

    for (const river of selectedRivers) {
      const stations = stationsByRiver[river] || [];
      const ids = stations.map(s => Number(s.station_id));
      const merged = {};

      for (const param of selectedParams) {
        const rows = await wqmsService.getSeriesMultiStations({
          stationIds: ids,
          param,
          start: `${fromDate}T00:00:00`,
          end: `${toDate}T23:59:59`,
          res: "hourly",
        });

        const avg = avgByStation(rows);

        stations.forEach((s) => {
  const o = Number(s.river_order);
  if (!merged[o]) {
    merged[o] = {
      order: o,
      station_id: Number(s.station_id),
      code: WQMS_LABELS[s.station_id]?.code || `WQMS${s.station_id}`,
      name: WQMS_LABELS[s.station_id]?.name || "",
    };
  }
  const a = avg.get(Number(s.station_id));
  merged[o][param] = a ? a.sum / a.n : null;
});

      }

      result[river] = Object.values(merged).sort((a, b) => a.order - b.order);
    }

    setChartDataByRiver(result);
    setLoading(false);
  };

  return (
    <div className="rpc-page">
      <div className="rpc-wrap">

        <div className="rpc-header">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} /> Back
          </button>
          <div className="rpc-header-main">
            <h2 className="rpc-title">Multi-River Upstream → Downstream Analysis</h2>
            <p className="rpc-subtitle">Longitudinal profiles and station maps by river (PKT).</p>
          </div>
        </div>

        {/* CONTROLS */}
       <div className="rpc-card rpc-controls">
          <div className="rpc-controls-head">Report filters</div>
          <div className="rpc-controls-body">
  {/* DATE RANGE */}
  <div className="rpc-controlsBlock">
    <div className="rpc-controlsBlockTitle">Date Range (PKT)</div>

    <div className="rpc-controls2col">
      <div className="rpc-field">
        <label>From</label>
        <input
          className="rpc-input"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
      </div>

      <div className="rpc-field">
        <label>To</label>
        <input
          className="rpc-input"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
      </div>
    </div>
  </div>

  {/* VALUES 
  <div className="rpc-controlsBlock">
    <div className="rpc-controlsBlockTitle">Values</div>
    <select
      className="rpc-select"
      value={valueMode}
      onChange={(e) => setValueMode(e.target.value)}
    >
      <option value="measured">Measured</option>
      <option value="clean">Clean</option>
    </select>
  </div>
*/}
  {/* RIVERS */}
  <div className="rpc-controlsBlock">
    <div className="rpc-controlsBlockTitle">Rivers (Multi)</div>
    <select
      multiple
      className="rpc-multi"
      value={selectedRivers}
      onChange={(e) =>
        setSelectedRivers(Array.from(e.target.selectedOptions).map((o) => o.value))
      }
    >
      {RIVERS.map((r) => (
        <option key={r} value={r}>{r}</option>
      ))}
    </select>
  </div>

  {/* PARAMETERS */}
  <div className="rpc-controlsBlock">
    <div className="rpc-controlsBlockTitle">Parameters (Multi)</div>
    <select
      multiple
      className="rpc-multi"
      value={selectedBaseParams}
      onChange={(e) =>
        setSelectedBaseParams(Array.from(e.target.selectedOptions).map((o) => o.value))
      }
    >
      {PARAM_DEFS.map((p) => (
        <option key={p.base} value={p.base}>{p.label}</option>
      ))}
    </select>
  </div>

          </div>
          <div className="rpc-controls-footer">
            <button className="rpc-btn" onClick={generateProfile} disabled={loading}>
              {loading ? "Generating…" : "Generate Profile"}
            </button>
          </div>
</div>


        {/* MAP */}
        <div className="rpc-card">
          <MapContainer center={[31.5, 74.3]} zoom={6} style={{ height: 420 }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {Object.entries(stationsByRiver).map(([river, sts]) => (
              <React.Fragment key={river}>
                <Polyline positions={sts.map(s => [s.latitude, s.longitude])}
                  pathOptions={{ color: RIVER_COLORS[river], weight: 5 }} />
                {sts.map(s => (
                  <Marker key={s.station_id}
                    position={[s.latitude, s.longitude]}
                    icon={stationIcon(s.station_id, RIVER_COLORS[river])}>
                    <LTooltip>
                      <strong>{WQMS_LABELS[s.station_id]?.code}</strong><br />
                      {WQMS_LABELS[s.station_id]?.name}
                    </LTooltip>
                  </Marker>
                ))}
              </React.Fragment>
            ))}
          </MapContainer>
        </div>

        {/* CHARTS */}
        {Object.entries(chartDataByRiver).map(([river, data]) => (
          <div key={river} className="rpc-card" style={{ marginBottom: 28 }}>
           <h3
  style={{
    color: RIVER_COLORS[river],
    padding: "10px 0",   // ✅ top/bottom padding
    margin: "0 0 8px 0", // ✅ spacing before chart
    lineHeight: 1.2,
  }}
>
  {river} – Longitudinal Profile
</h3>


            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="4 4" opacity={0.35} />
<XAxis
  dataKey="order"
  interval={0}
  allowDecimals={false}
  height={48}                // ✅ gives more vertical room
  tickMargin={14}            // ✅ more space from axis line
  padding={{ left: 18, right: 18 }} // ✅ prevents edge cut
  tick={{ fontSize: 12, fill: "#cbd5e1" }} // ✅ smaller/cleaner
  tickFormatter={(v) => {
    const r = data?.find((d) => Number(d.order) === Number(v));
    return r?.code || v;
  }}
/>


<Tooltip
  content={(props) => (
    <ProfileTooltip
      {...props}
      fromDate={fromDate}
      toDate={toDate}
      valueMode={valueMode}
    />
  )}
/>


<Legend />

                {selectedParams.map((p, i) => (
                  <Line key={p}
                    dataKey={p}
                    name={PARAM_DEFS.find(d => d.measured === p || d.clean === p)?.label}
                    stroke={SOFT_COLORS[i % SOFT_COLORS.length]}
					strokeWidth={2.4}
                    dot={{ r: 3 }}
activeDot={{ r: 5 }}
                    connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ))}

      </div>
    </div>
  );
}
